import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { getRunBoard, TRunBoardItem } from '../../../requests/opsScheduling';
import { btnSecondary, Card, PageBody, PageHeader, Pill, Toolbar } from '../ui';

const hhmm = (iso: string | null) =>
  iso ? new Date(iso).toISOString().slice(11, 16) : '—';
const day = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : '—';

/**
 * The morning run board (Eddie, 2026-09-25): every class running today,
 * tomorrow, this week and the next two — name, account, trainer, start
 * and end, room, location — with anything that would stop it from running
 * flagged on the line. Refreshes itself every five minutes; prints as a
 * sheet.
 */
const OpsRunBoard: React.FC = () => {
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['opsRunBoard'],
    queryFn: () => getRunBoard(14),
    refetchInterval: 5 * 60 * 1000,
  });

  const row = (c: TRunBoardItem) => (
    <li
      key={c.id}
      className={`grid grid-cols-[auto_1fr] gap-x-3 px-4 py-2 text-sm ${
        c.flags.length ? 'bg-amber-50/60' : ''
      }`}
    >
      <div className="whitespace-nowrap font-mono text-xs text-gray-bold [font-variant-numeric:tabular-nums]">
        <div>{day(c.start)}</div>
        <div>
          {hhmm(c.start)}–{hhmm(c.end)}
          {c.timeZone ? ` ${c.timeZone}` : ' UTC'}
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2">
          <Link
            className="font-medium text-gray-four hover:text-blue-primary hover:underline"
            to={PATH.opsClassEdit.replace(':id', c.id)}
          >
            {c.name ?? '(unnamed class)'}
          </Link>
          {c.classType === 'Private Class' ? (
            <Pill value="private" tone="info" />
          ) : (
            <Pill value="public" />
          )}
          {c.reservationStatus && (
            <Pill value={c.reservationStatus} tone="neutral" />
          )}
          {c.flags.map((f) => (
            <Pill key={f} value={f} tone="warn" />
          ))}
        </div>
        <div className="text-xs text-gray-five">
          {c.accountId ? (
            <Link
              className="hover:text-blue-primary hover:underline"
              to={PATH.opsAccountView.replace(':id', c.accountId)}
            >
              {c.accountName ?? 'no account'}
            </Link>
          ) : (
            c.accountName ??
            (c.classType === 'Public Class' ? 'public' : 'no account')
          )}
          {' · '}
          {c.instructor ?? (
            <span className="text-amber-800">no instructor</span>
          )}
          {' · '}
          {c.location === 'Onsite' ? (
            <span>
              on site{c.locationAddress ? ` — ${c.locationAddress}` : ''}
            </span>
          ) : c.joinUrl ? (
            <a
              className="text-blue-primary hover:underline"
              href={c.joinUrl}
              target="_blank"
              rel="noreferrer"
            >
              {c.room ?? 'Zoom'}
            </a>
          ) : (
            <span>{c.room ?? c.videoProvider ?? 'online'}</span>
          )}
          {' · '}
          {c.registered}
          {c.seats != null ? ` / ${c.seats}` : ''} registered
          {c.prepCallStatus ? ` · prep call ${c.prepCallStatus}` : ''}
        </div>
      </div>
    </li>
  );

  return (
    <>
      <PageHeader
        title="Run board"
        meta={
          data
            ? `${data.total} classes in the next ${data.days} days · ${
                data.flagged
              } flagged · updated ${new Date(
                dataUpdatedAt,
              ).toLocaleTimeString()}`
            : 'Classes running today, tomorrow, this week and the next two'
        }
        actions={
          <button className={btnSecondary} onClick={() => window.print()}>
            Print
          </button>
        }
      />
      <Toolbar>
        <label className="flex items-center gap-1.5 text-sm text-gray-five">
          <input
            type="checkbox"
            className="ops-check"
            checked={flaggedOnly}
            onChange={(e) => setFlaggedOnly(e.target.checked)}
          />
          Flagged only
        </label>
        <span className="text-xs text-gray-bold">
          A flag is something that would stop the class running: no instructor,
          no room for an online class, no address for an onsite one, nobody
          registered, a hold not yet confirmed.
        </span>
      </Toolbar>
      {isLoading || !data ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <PageBody wide>
          <div className="grid gap-4 xl:grid-cols-2">
            {data.buckets.map((b) => {
              const items = b.items.filter(
                (c) => !flaggedOnly || c.flags.length,
              );
              return (
                <Card
                  key={b.key}
                  title={`${b.label} · ${b.items.length}${
                    b.items.filter((c) => c.flags.length).length
                      ? ` · ${
                          b.items.filter((c) => c.flags.length).length
                        } flagged`
                      : ''
                  }`}
                >
                  {items.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-bold">
                      {b.items.length === 0
                        ? 'Nothing scheduled.'
                        : 'Nothing flagged.'}
                    </div>
                  ) : (
                    <ul className="divide-y divide-white-three">
                      {items.map(row)}
                    </ul>
                  )}
                </Card>
              );
            })}
          </div>
        </PageBody>
      )}
    </>
  );
};

export default OpsRunBoard;
